import { Box } from "@mui/material";
import { EmblaCarouselType } from "embla-carousel";
import useEmblaCarousel from "embla-carousel-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMounted, UseMountedProps } from "../../hooks/common";
import { useDeviceStyles } from "../../hooks/useDeviceStyles";
import { transitions } from "../../styles/theme";
import { FlexOrderCarousel } from "../FlexOrderCarousel/FlexOrderCarousel";
import { IconArrowLeft2, IconArrowRight2 } from "../Icons/Icons";
import { ArrowControlCommon, Container } from "./HybridCarousel.styled";

export type HybridCarouselProps = {
  itemWidth: number;
  children: React.ReactNode;
  activeIndices: number[];
  orders?: number[];
  gap?: number;
  className?: string;
  onSlideChange?: (index: number, api?: EmblaCarouselType | undefined) => void;
} & UseMountedProps &
  React.ComponentProps<typeof Container>;

export function HybridCarousel({
  itemWidth,
  children,
  activeIndices,
  orders = [],
  gap = 0,
  onSlideChange,
  anchor,
  sx,
  className,
}: HybridCarouselProps) {
  const [visible, setVisible] = useState(false);
  const [prevEnabled, setPrevEnabled] = useState(false);
  const [nextEnabled, setNextEnabled] = useState(false);
  const [availableWidth, setAvailableWidth] = useState(0);

  const { mounted } = useMounted({ anchor });
  const { isMobileDevice } = useDeviceStyles();

  const rootRef = useRef<HTMLDivElement | null>(null);

  const items = React.Children.toArray(children);
  const itemCount = items.length;
  const activeIndex = activeIndices[activeIndices.length - 1] ?? 0;

  const activeIndexRef = useRef(activeIndex);
  activeIndexRef.current = activeIndex;

  const CAROUSEL_HEIGHT = isMobileDevice ? 80 : 190;

  const totalContentWidth = useMemo(() => {
    if (itemCount <= 0) return 0;
    return itemCount * itemWidth + Math.max(0, itemCount - 1) * gap;
  }, [itemCount, itemWidth, gap]);

  const isPropulsion = isMobileDevice ? itemWidth === 132 : itemWidth === 230;
  const isExtras = isMobileDevice ? itemWidth === 108 : itemWidth === 180;

  /*const visibleCountWhenScrolling = useMemo(() => {
    switch (itemWidth) {
      case 230:
        return 3;
      case 180:
        return 5;
      case 132:
        return 3;
      case 110:
        return 5;
      case 108:
        return 3;
      case 66:
        if (5 * 66 + 80 > availableWidth) return 3;
        return 5;
      default:
        return 7;
    }
  }, [itemWidth]);*/
  const visibleCountWhenScrolling = useMemo(() => {
    if (!availableWidth) return 3;

    // Platz für die seitlichen Pfeile reservieren – nur für die Zählung der
    // sichtbaren Items, nicht für die shouldScroll-Entscheidung (sonst scrollen
    // Kategorien mit wenigen, breiten Items unnötig, z.B. Propulsion).
    const ARROW_SAFE_SPACE = isMobileDevice ? 80 : 100;
    const usableWidth = Math.max(0, availableWidth - ARROW_SAFE_SPACE);

    const rawCount = Math.floor((usableWidth + gap) / (itemWidth + gap));

    let count = Math.max(3, rawCount);

    if (count % 2 === 0) {
      count -= 1;
    }

    return Math.min(count, itemCount);
  }, [availableWidth, itemWidth, gap, isMobileDevice, itemCount]);

  const canLoop = useMemo(() => {
    if (!availableWidth) return false;
    const rowCount = Math.min(visibleCountWhenScrolling, itemCount);
    const rowWidth = rowCount * itemWidth + Math.max(0, rowCount - 1) * gap;
    const viewport = Math.min(rowWidth, availableWidth);
    return totalContentWidth - viewport >= itemWidth + gap;
  }, [
    availableWidth,
    visibleCountWhenScrolling,
    itemCount,
    itemWidth,
    gap,
    totalContentWidth,
  ]);

  const shouldScroll =
    itemCount > 2 &&
    availableWidth > 0 &&
    totalContentWidth > availableWidth &&
    canLoop;

  const visibleCount = shouldScroll
    ? Math.min(visibleCountWhenScrolling, itemCount)
    : itemCount;

  const viewportWidth = useMemo(() => {
    if (!shouldScroll) return "100%";
    const rowWidth =
      visibleCount * itemWidth + Math.max(0, visibleCount - 1) * gap;
    // gutter so the absolute arrows aren't clipped by the wrapper overflow
    const gutter = isPropulsion || isExtras ? (isMobileDevice ? 16 : 0) : 44;
    const cap = Math.max(itemWidth, availableWidth - 2 * gutter);
    return `${Math.min(rowWidth, cap)}px`;
  }, [
    shouldScroll,
    visibleCount,
    itemWidth,
    gap,
    availableWidth,
    isPropulsion,
    isExtras,
    isMobileDevice,
  ]);

  const options = useMemo(
    () => ({
      align: "center" as const,
      loop: true,
      slidesToScroll: 1,
      skipSnaps: false,
      containScroll: false as const,
      startIndex: Math.max(0, Math.min(activeIndexRef.current, itemCount - 1)),
    }),
    [itemCount, shouldScroll],
  );

  const arrowYOffset = useMemo(() => {
    switch (itemWidth) {
      case 230:
        return "-77.5%"; // propulsion
      case 180:
        return "-77.5%"; // extras
      case 132:
        return "-50%";
      case 110:
        return "-65%"; // propulsion mobile
      case 108:
        return "-50%"; // extras mobile
      case 66:
        return "-55%"; // hull / flooring / fabric mobile
      default:
        return "-65%"; // hull / flooring / fabric
    }
  }, [itemWidth]);

  const [emblaRef, emblaApi] = useEmblaCarousel(options);

  const handleSlideClick = useCallback(
    (index: number, instant?: boolean) => {
      if (shouldScroll) {
        if (!emblaApi) return;
        emblaApi.scrollTo(index, instant);
      }
      onSlideChange?.(index, emblaApi);
    },
    [emblaApi, onSlideChange, shouldScroll],
  );

  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext(true);
  }, [emblaApi]);

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev(true);
  }, [emblaApi]);

  const refreshArrows = useCallback(() => {
    if (!emblaApi) return;
    setPrevEnabled(emblaApi.canScrollPrev());
    setNextEnabled(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return;

    const measure = () => {
      const next = Math.floor(node.clientWidth);
      setAvailableWidth((prev) => (prev === next ? prev : next));
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!emblaApi || !shouldScroll) {
      setPrevEnabled(false);
      setNextEnabled(false);
      return;
    }

    const onSelect = () => {
      requestAnimationFrame(() => {
        const idx = emblaApi.selectedScrollSnap();

        if (idx !== activeIndex) {
          onSlideChange?.(idx, emblaApi);
        }

        refreshArrows();
      });
    };

    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
      emblaApi.off("reInit", onSelect);
    };
  }, [emblaApi, onSlideChange, shouldScroll, activeIndex, refreshArrows]);

  useEffect(() => {
    if (!emblaApi || !shouldScroll) return;

    const current = emblaApi.selectedScrollSnap();
    if (current === activeIndex) return;

    emblaApi.scrollTo(activeIndex, true);
  }, [emblaApi, activeIndex, shouldScroll]);

  useEffect(() => {
    if (mounted && !visible && availableWidth > 0) {
      const t = window.setTimeout(() => setVisible(true), 100);
      return () => window.clearTimeout(t);
    }
  }, [mounted, visible, availableWidth]);

  return (
    <Box
      ref={rootRef}
      className={className}
      sx={{
        mx: "auto",
        width: "100%",
        height: CAROUSEL_HEIGHT,
        overflow: "hidden",
        opacity: visible ? 1 : 0,
        transition: transitions.join(["opacity"]),
      }}
    >
      <Container
        key={anchor}
        sx={[
          {
            width: "100%",
            height: "100%",
            position: "relative",
            padding: 0,

            ".embla__slide": {
              flex: "0 0 auto",
              width: `${itemWidth}px`,
            },
            ".item": {
              flex: "1 1 0",
              minWidth: 0,
              maxWidth: `${itemWidth}px`,
            },
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        {shouldScroll ? (
          <Box
            sx={{
              position: "relative",
              height: "100%",
              width: viewportWidth,
              maxWidth: "100%",
              mx: "auto",
            }}
          >
            {/* EMBLA */}
            <Box
              ref={emblaRef}
              className="embla"
              sx={{
                overflow: "hidden",
                height: "100%",
                // soft fade at the edges so slides don't hard-cut next to the arrows
                "--fade": "28px",
                maskImage:
                  "linear-gradient(90deg, transparent 0, #000 var(--fade), #000 calc(100% - var(--fade)), transparent 100%)",
                WebkitMaskImage:
                  "linear-gradient(90deg, transparent 0, #000 var(--fade), #000 calc(100% - var(--fade)), transparent 100%)",
              }}
            >
              <Box
                className="embla__container"
                sx={{
                  display: "flex",
                  gap: `${gap}px`,
                  height: "100%",
                }}
              >
                {items.map((c, i) => (
                  <Box
                    key={i}
                    className="embla__slide"
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleSlideClick(i)}
                  >
                    {c}
                  </Box>
                ))}
              </Box>
            </Box>

            {/* LEFT */}
            {prevEnabled && (
              <ArrowControlCommon
                onClick={scrollPrev}
                sx={{
                  position: "absolute",
                  left:
                    isMobileDevice && (isPropulsion || isExtras)
                      ? -12
                      : isPropulsion || isExtras
                        ? 0
                        : -40,

                  top: "50%",
                  transform: `translateY(${arrowYOffset})`,
                  zIndex: 2,
                  svg: {
                    width: isMobileDevice ? "28px" : "42px",
                    height: isMobileDevice ? "68px" : "102px",
                    display: "block",
                  },
                }}
              >
                {isMobileDevice ? <IconArrowLeft2 /> : <IconArrowLeft2 />}
              </ArrowControlCommon>
            )}

            {/* RIGHT */}
            {nextEnabled && (
              <ArrowControlCommon
                onClick={scrollNext}
                sx={{
                  position: "absolute",

                  right:
                    isMobileDevice && (isPropulsion || isExtras)
                      ? -12
                      : isPropulsion || isExtras
                        ? 0
                        : -40,
                  top: "50%",
                  transform: `translateY(${arrowYOffset})`,
                  zIndex: 2,
                  svg: {
                    width: isMobileDevice ? "28px" : "42px",
                    height: isMobileDevice ? "68px" : "102px",
                    display: "block",
                  },
                }}
              >
                {isMobileDevice ? <IconArrowRight2 /> : <IconArrowRight2 />}
              </ArrowControlCommon>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              gap: `${gap}px`,
              height: "100%",
              width: "100%",
            }}
          >
            {items.map((el, i) => (
              <Box
                key={i}
                className="item"
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  cursor: "pointer",
                }}
                onClick={() => handleSlideClick(i, true)}
              >
                {el}
              </Box>
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
}
